import { motion, AnimatePresence } from "framer-motion";
import { 
  Building2, MapPin, Check, X, Wifi, Car, Coffee, 
  ChevronDown, ChevronUp, ArrowUp, ArrowDown
} from "lucide-react";

const wifiLabels = {
  very_fast: "Very Fast", fast: "Fast", good: "Good", poor: "Poor", none: "None",
  excellent: "Excellent", fair: "Fair"
};

const noiseLabels = { quiet: "Quiet", moderate: "Moderate", lively: "Lively" };

export default function AdminSubmissionsTab({
  filteredSubmissions,
  sortConfig,
  handleSort,
  expandedItem,
  setExpandedItem,
  processing,
  handleApprove,
  openRejectModal
}) {
  return (
    <div className="space-y-3">
      {/* Sort Controls */}
      <div className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-4 flex-wrap">
        <span className="text-sm font-medium text-gray-600">Sort by:</span>
        <button
          onClick={() => handleSort('submissions', 'name')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${sortConfig.submissions?.field === 'name' ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="sort-submissions-name"
        >
          Name
          {sortConfig.submissions?.field === 'name' && (
            sortConfig.submissions.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
          )}
        </button>
        <button
          onClick={() => handleSort('submissions', 'city')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${sortConfig.submissions?.field === 'city' ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="sort-submissions-city"
        >
          City
          {sortConfig.submissions?.field === 'city' && (
            sortConfig.submissions.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
          )}
        </button>
        <button
          onClick={() => handleSort('submissions', 'venue_type')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${sortConfig.submissions?.field === 'venue_type' ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="sort-submissions-type"
        >
          Type
          {sortConfig.submissions?.field === 'venue_type' && (
            sortConfig.submissions.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
          )}
        </button>
        <button
          onClick={() => handleSort('submissions', 'created_at')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${sortConfig.submissions?.field === 'created_at' ? 'bg-brand/10 text-brand' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="sort-submissions-date"
        >
          Date
          {sortConfig.submissions?.field === 'created_at' && (
            sortConfig.submissions.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
          )}
        </button>
        <span className="ml-auto text-sm text-gray-500">{filteredSubmissions.length} submission{filteredSubmissions.length !== 1 ? 's' : ''}</span>
      </div>
      
      {filteredSubmissions.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center">
          <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900">All caught up!</h3>
          <p className="text-gray-500 mt-1">No pending submissions.</p>
        </div>
      ) : filteredSubmissions.map((sub) => (
        <motion.div 
          key={sub.id} 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="bg-white rounded-xl shadow-sm overflow-hidden"
        >
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50" 
            onClick={() => setExpandedItem(expandedItem === sub.id ? null : sub.id)} 
            data-testid={`expand-${sub.id}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${sub.is_owner_submission ? 'bg-purple-100' : 'bg-amber-100'} rounded-lg flex items-center justify-center`}>
                <Building2 className={`w-6 h-6 ${sub.is_owner_submission ? 'text-purple-600' : 'text-amber-600'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{sub.name}</h3>
                  {sub.is_owner_submission && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                      Owner Submission
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500"><MapPin className="w-3.5 h-3.5" />{sub.city} • {sub.venue_type?.replace("_", " ")}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">by {sub.submitter_name}</span>
              {expandedItem === sub.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </div>
          </div>
          <AnimatePresence>
            {expandedItem === sub.id && (
              <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                <div className="px-4 pb-4 border-t border-gray-100 pt-4">
                  {/* Submission Images */}
                  {sub.images && sub.images.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Submitted Photos ({sub.images.length})</p>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {sub.images.map((img, idx) => (
                          <img
                            key={img.filename || img.url || `img-${idx}`}
                            src={img.url || img}
                            alt={`Submission photo ${idx + 1}`}
                            className="w-24 h-24 object-cover rounded-lg flex-shrink-0 border border-gray-200"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <p className="text-sm"><strong>Description:</strong> {sub.description}</p>
                      <p className="text-sm"><strong>Address:</strong> {sub.address}, {sub.city} {sub.postcode}</p>
                      <p className="text-sm"><strong>Contact:</strong> {sub.submitter_email}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs"><Wifi className="w-3 h-3 inline mr-1" />{wifiLabels[sub.wifi_quality]}</span>
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">{noiseLabels[sub.noise_level]} Noise</span>
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">{sub.power_outlets} Outlets</span>
                      {sub.has_parking && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs"><Car className="w-3 h-3 inline mr-1" />Parking</span>}
                      {sub.has_food && <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs"><Coffee className="w-3 h-3 inline mr-1" />Food</span>}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => handleApprove(sub)} disabled={processing === sub.id} className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`approve-${sub.id}`}>
                      <Check className="w-4 h-4" /> Approve
                    </button>
                    <button onClick={() => openRejectModal(sub)} disabled={processing === sub.id} className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`reject-${sub.id}`}>
                      <X className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>
  );
}
